/**
 * FloorPulseStrip — slim "Today on the floor" strip shown at the top of the
 * Cashier Dashboard. Polls /api/cashier/floor-pulse every 10 s and animates
 * the values when they change so the cashier can feel the room's momentum.
 *
 * Designed to be unobtrusive (single row, ~80 px tall) on desktop and to
 * wrap gracefully on mobile.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
    Users as UsersIcon, Banknote, Gift, TrendingUp, UserPlus, Activity,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 10_000;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCount = (n) => Number(n || 0).toLocaleString('en-US');

const Tile = ({ icon: Icon, label, value, tone = 'neutral', flash, testId, sublabel }) => {
    const toneCls = {
        neutral: 'text-zinc-100',
        green: 'text-emerald-300',
        red: 'text-rose-300',
        amber: 'text-amber-300',
        sky: 'text-sky-300',
    }[tone] || 'text-zinc-100';
    return (
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-md border border-zinc-800 bg-zinc-950/60 transition ${flash ? 'ring-1 ring-emerald-400/40' : ''}`}
            data-testid={testId}>
            <Icon className={`w-4 h-4 ${toneCls} flex-shrink-0`} />
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 leading-none">{label}</div>
                <div className={`text-base font-mono font-semibold mt-1 leading-none ${toneCls}`}>{value}</div>
                {sublabel && <div className="text-[10px] text-zinc-500 mt-1 leading-none">{sublabel}</div>}
            </div>
        </div>
    );
};

const FloorPulseStrip = () => {
    const [pulse, setPulse] = useState(null);
    const [error, setError] = useState(false);
    const [flashKey, setFlashKey] = useState(0);
    const prevRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        let timer;

        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/cashier/floor-pulse`, { withCredentials: true });
                if (!mounted) return;
                // Flash green ring on tiles when key values jump
                const prev = prevRef.current;
                if (prev) {
                    const changed = prev.playing_now !== data.playing_now
                        || prev.loaded_today !== data.loaded_today
                        || prev.cashed_today !== data.cashed_today
                        || prev.freeplay_today !== data.freeplay_today
                        || prev.new_signups_today !== data.new_signups_today;
                    if (changed) setFlashKey(k => k + 1);
                }
                prevRef.current = data;
                setPulse(data);
                setError(false);
            } catch (err) {
                if (!mounted) return;
                console.error('floor-pulse error', err);
                setError(true);
            }
        };

        load();
        timer = setInterval(load, POLL_MS);
        return () => { mounted = false; clearInterval(timer); };
    }, []);

    if (error) {
        return (
            <div className="text-[11px] text-zinc-500 px-4 py-2" data-testid="floor-pulse-error">
                Live floor data unavailable.
            </div>
        );
    }
    if (!pulse) {
        return (
            <div className="text-[11px] text-zinc-600 px-4 py-2" data-testid="floor-pulse-loading">
                Loading floor pulse…
            </div>
        );
    }

    const net = pulse.net_today;
    const netTone = net >= 0 ? 'green' : 'red';

    return (
        <div className="mb-4" data-testid="floor-pulse-strip">
            <div className="flex items-center gap-2 mb-2">
                <Activity className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
                    Today on the floor · {pulse.operator_name}
                </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
                key={flashKey /* re-mount to retrigger flash */}>
                <Tile icon={UsersIcon} label="Playing now" tone="green"
                    value={fmtCount(pulse.playing_now)}
                    sublabel="last 60 min"
                    flash
                    testId="floor-pulse-playing-now" />
                <Tile icon={Banknote} label="On the floor" tone="sky"
                    value={fmtMoney(pulse.dollars_on_floor)}
                    sublabel="active player credits"
                    flash
                    testId="floor-pulse-dollars" />
                <Tile icon={TrendingUp} label="Loaded today"
                    value={fmtMoney(pulse.loaded_today)}
                    flash
                    testId="floor-pulse-loaded" />
                <Tile icon={Banknote} label="Cashed today"
                    value={fmtMoney(pulse.cashed_today)}
                    flash
                    testId="floor-pulse-cashed" />
                <Tile icon={Gift} label="Free play today" tone="amber"
                    value={fmtMoney(pulse.freeplay_today)}
                    flash
                    testId="floor-pulse-freeplay" />
                <Tile icon={UserPlus} label={`Net · ${pulse.new_signups_today} new`} tone={netTone}
                    value={`${net >= 0 ? '+' : ''}${fmtMoney(net)}`}
                    sublabel="load − cashout"
                    flash
                    testId="floor-pulse-net" />
            </div>
        </div>
    );
};

export default FloorPulseStrip;
